import type { Project } from '../types';

interface ProjectCardProps {
  project: Project;
  onClick: () => void;
  showStatus?: boolean;
}

export default function ProjectCard({ project, onClick, showStatus = false }: ProjectCardProps) {
  const logoUrl = project.logo?.thumbnailUrl || project.logo?.url;

  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px',
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        textAlign: 'left',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        minWidth: 0,
        position: 'relative',
        margin: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
      }}
    >
      {/* Logo */}
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={project.name}
          style={{
            width: '48px',
            height: '48px',
            minWidth: '48px',
            maxWidth: '48px',
            minHeight: '48px',
            maxHeight: '48px',
            borderRadius: '10px',
            objectFit: 'cover',
            flexShrink: 0,
            display: 'block',
          }}
        />
      ) : (
        <div style={{
          width: '48px',
          height: '48px',
          minWidth: '48px',
          maxWidth: '48px',
          borderRadius: '10px',
          background: 'linear-gradient(135deg, #9945FF, #14F195)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          fontSize: '20px',
        }}>
          🚀
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', maxWidth: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, width: '100%', maxWidth: '100%' }}>
          <div style={{
            fontSize: '14px',
            fontWeight: 600,
            color: 'white',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
            maxWidth: '100%',
          }}>
            {project.name}
          </div>
          
          {/* X Verified Badge */}
          {project.xVerifiedAt && (
            <div style={{
              width: '16px',
              height: '16px',
              minWidth: '16px',
              maxWidth: '16px',
              borderRadius: '50%',
              background: '#1d9bf0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <svg style={{ width: '10px', height: '10px', color: 'white' }} viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
          )}

          {/* Status Badge */}
          {showStatus && (
            <span style={{
              fontSize: '10px',
              fontWeight: 600,
              padding: '2px 6px',
              borderRadius: '4px',
              background: project.status === 'published' 
                ? 'rgba(20, 241, 149, 0.2)' 
                : 'rgba(255, 193, 7, 0.2)',
              color: project.status === 'published' 
                ? '#14F195' 
                : '#ffc107',
              textTransform: 'uppercase',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}>
              {project.status}
            </span>
          )}
        </div>

        {/* Tagline */}
        {project.tagline && (
          <div style={{
            fontSize: '12px',
            color: 'rgba(255,255,255,0.5)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginTop: '2px',
            width: '100%',
          }}>
            {project.tagline}
          </div>
        )}

        {/* Team */}
        {project.team && (
          <div style={{
            fontSize: '11px',
            color: 'rgba(153, 69, 255, 0.8)',
            marginTop: '4px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
          }}>
            by {project.team.name}
          </div>
        )}
      </div>

      {/* Arrow */}
      <svg
        style={{ width: '16px', height: '16px', minWidth: '16px', maxWidth: '16px', color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M9 18l6-6-6-6"/>
      </svg>
    </button>
  );
}
